import { Button } from '@/components/ui/Button';
import { Loading } from '@/components/ui/Loading';
import { cn } from '@/utils/cn';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Mail,
  MailCheck,
  MailWarning,
  RefreshCw,
  RotateCcw,
  Send,
  UserRound,
} from 'lucide-react';
import { useState } from 'react';
import {
  useAdminSupportReply,
  useAdminSupportStats,
  useAdminSupportTicket,
  useAdminSupportTickets,
  useCloseAdminSupportTicket,
  useRetryAdminSupportEmail,
  useUpdateAdminSupportStatus,
} from '../hooks/useSupportTickets';
import type {
  AdminSupportQueue,
  AdminSupportTicket,
  SupportTicketReply,
  SupportTicketStatus,
} from '../types/support.types';

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: 'À traiter',
  in_progress: 'À traiter',
  waiting_for_user: 'En attente du client',
  resolved: 'Résolue',
  closed: 'Clôturée',
};

const STATUS_STYLES: Record<SupportTicketStatus, string> = {
  open: 'bg-warning-50 text-warning-800 ring-warning-200',
  in_progress: 'bg-warning-50 text-warning-800 ring-warning-200',
  waiting_for_user: 'bg-primary-50 text-primary-800 ring-primary-200',
  resolved: 'bg-success-50 text-success-800 ring-success-200',
  closed: 'bg-neutral-100 text-neutral-700 ring-neutral-200',
};

const QUEUES: Array<{ value: AdminSupportQueue; label: string }> = [
  { value: 'action_required', label: 'À traiter' },
  { value: 'waiting', label: 'En attente' },
  { value: 'done', label: 'Terminées' },
];

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : dateFormatter.format(date);
}

function shortReference(uuid: string) {
  return uuid.slice(0, 8).toUpperCase();
}

function StatusBadge({ status }: { status: SupportTicketStatus }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
        STATUS_STYLES[status]
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function EmailSourceBadge() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
      <Mail className="h-3 w-3" aria-hidden="true" />
      E-mail
    </span>
  );
}

function TicketRow({
  ticket,
  onSelect,
}: {
  ticket: AdminSupportTicket;
  onSelect: () => void;
}) {
  const personName = ticket.user
    ? `${ticket.user.firstName ?? ''} ${ticket.user.lastName ?? ''}`.trim()
    : ticket.requesterName || '';
  const personEmail = ticket.user?.email || ticket.requesterEmail;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="group focus-visible:ring-primary-500 flex w-full items-start justify-between gap-4 border-b border-neutral-100 px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset sm:items-center"
      aria-label={`Ouvrir la demande ${ticket.subject}`}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate font-medium text-neutral-900">
            {ticket.subject}
          </p>
          {ticket.origin === 'email' && <EmailSourceBadge />}
        </div>
        <p className="mt-1 truncate text-sm text-neutral-600">
          {personName || personEmail || 'Expéditeur inconnu'}
          {personName && personEmail ? ` · ${personEmail}` : ''}
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          #{shortReference(ticket.uuid)} · activité{' '}
          {formatDate(ticket.updatedAt)}
          {ticket.replyCount > 0
            ? ` · ${ticket.replyCount} message${ticket.replyCount > 1 ? 's' : ''}`
            : ''}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <StatusBadge status={ticket.status} />
        <ChevronRight className="h-4 w-4 text-neutral-400 transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}

function DeliveryState({
  ticketUuid,
  reply,
}: {
  ticketUuid: string;
  reply: SupportTicketReply;
}) {
  const retry = useRetryAdminSupportEmail(ticketUuid, reply.uuid);

  if (reply.authorRole !== 'admin' || !reply.emailDeliveryStatus) return null;

  if (reply.emailDeliveryStatus === 'sent') {
    return (
      <p className="text-success-700 mt-3 flex items-center gap-1.5 text-xs">
        <MailCheck className="h-3.5 w-3.5" />
        Notification e-mail envoyée
      </p>
    );
  }

  if (reply.emailDeliveryStatus === 'pending') {
    return <p className="mt-3 text-xs text-neutral-500">Envoi en cours…</p>;
  }

  return (
    <div className="bg-error-50 text-error-900 mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm">
      <span className="flex items-center gap-2">
        <MailWarning className="h-4 w-4 shrink-0" />
        E-mail non envoyé. La réponse reste enregistrée ici.
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => retry.mutate()}
        disabled={retry.isPending}
        className="gap-1.5"
      >
        <RefreshCw
          className={cn('h-3.5 w-3.5', retry.isPending && 'animate-spin')}
        />
        {retry.isPending ? 'Nouvel envoi…' : "Relancer l'e-mail"}
      </Button>
    </div>
  );
}

function AdminTicketDetail({
  ticketUuid,
  onBack,
}: {
  ticketUuid: string;
  onBack: () => void;
}) {
  const {
    data: ticket,
    isLoading,
    isError,
    refetch,
  } = useAdminSupportTicket(ticketUuid);
  const [reply, setReply] = useState('');
  const replyMutation = useAdminSupportReply(ticketUuid);
  const closeMutation = useCloseAdminSupportTicket(ticketUuid);
  const statusMutation = useUpdateAdminSupportStatus(ticketUuid);

  if (isLoading) return <Loading className="py-16" />;

  if (isError || !ticket) {
    return (
      <div className="border-error-200 bg-error-50 rounded-2xl border p-6 text-center">
        <AlertCircle className="text-error-600 mx-auto h-6 w-6" />
        <p className="text-error-900 mt-2 font-medium">
          Cette demande n’a pas pu être chargée.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button type="button" variant="ghost" onClick={onBack}>
            Retour
          </Button>
          <Button type="button" variant="outline" onClick={() => refetch()}>
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  const isDone = ticket.status === 'resolved' || ticket.status === 'closed';
  const personName = ticket.user
    ? `${ticket.user.firstName ?? ''} ${ticket.user.lastName ?? ''}`.trim()
    : ticket.requesterName || '';
  const personEmail = ticket.user?.email || ticket.requesterEmail;

  const submitReply = (event: React.FormEvent) => {
    event.preventDefault();
    const content = reply.trim();
    if (!content) return;
    replyMutation.mutate(content, { onSuccess: () => setReply('') });
  };

  return (
    <section aria-labelledby="admin-ticket-title" className="space-y-5">
      <div className="flex items-start gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="mt-0.5 -ml-2 shrink-0"
          aria-label="Revenir à la file de support"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              id="admin-ticket-title"
              className="text-xl font-semibold text-neutral-900"
            >
              {ticket.subject}
            </h2>
            <StatusBadge status={ticket.status} />
            {ticket.origin === 'email' && <EmailSourceBadge />}
          </div>
          <p className="mt-1 text-sm text-neutral-500">
            Demande #{shortReference(ticket.uuid)} ·{' '}
            {formatDate(ticket.createdAt)}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl bg-neutral-100 px-4 py-3">
        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />
        <div className="min-w-0 text-sm">
          <p className="font-medium text-neutral-900">
            {personName || personEmail || 'Expéditeur inconnu'}
          </p>
          {personEmail && (
            <a
              href={`mailto:${personEmail}`}
              className="text-primary-700 underline-offset-2 hover:underline"
            >
              {personEmail}
            </a>
          )}
        </div>
      </div>

      <article className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6">
        <p className="text-sm font-medium text-neutral-700">Demande initiale</p>
        <p className="mt-3 text-sm leading-6 whitespace-pre-wrap text-neutral-800">
          {ticket.content}
        </p>
      </article>

      {ticket.closureSource === 'external_email' && (
        <div className="flex items-start gap-2 rounded-xl bg-neutral-100 px-4 py-3 text-sm text-neutral-700">
          <CheckCircle2 className="text-success-600 mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Clôturée après une réponse apportée par e-mail hors de
            l’application. Aucun contenu de réponse n’a été inventé dans
            l’historique.
          </p>
        </div>
      )}

      {ticket.replies.length > 0 && (
        <div className="space-y-3" aria-label="Échanges du ticket">
          {ticket.replies.map(message => {
            const fromSupport = message.authorRole === 'admin';
            return (
              <article
                key={message.uuid}
                className={cn(
                  'rounded-2xl border p-5',
                  fromSupport
                    ? 'border-primary-200 bg-primary-50'
                    : 'border-neutral-200 bg-white'
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-neutral-900">
                    {fromSupport
                      ? 'Vous'
                      : message.authorName || personName || 'Client'}
                  </p>
                  <time
                    className="text-xs text-neutral-500"
                    dateTime={message.createdAt}
                  >
                    {formatDate(message.createdAt)}
                  </time>
                </div>
                <p className="mt-2 text-sm leading-6 whitespace-pre-wrap text-neutral-800">
                  {message.content}
                </p>
                <DeliveryState ticketUuid={ticketUuid} reply={message} />
              </article>
            );
          })}
        </div>
      )}

      {!isDone ? (
        <form
          onSubmit={submitReply}
          className="rounded-2xl border border-neutral-200 bg-white p-5"
        >
          <label
            htmlFor="admin-support-reply"
            className="block text-sm font-medium text-neutral-700"
          >
            Répondre au client
          </label>
          <textarea
            id="admin-support-reply"
            value={reply}
            onChange={event => setReply(event.target.value)}
            rows={5}
            className="focus:border-primary-500 focus:ring-primary-100 mt-2 block w-full resize-y rounded-xl border border-neutral-300 px-4 py-3 placeholder:text-neutral-400/50 focus:ring-2"
            placeholder="Écrivez une réponse claire et directement utile"
          />
          <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => closeMutation.mutate('admin_confirmed')}
              disabled={closeMutation.isPending}
            >
              Clôturer
            </Button>
            <Button
              type="submit"
              disabled={!reply.trim() || replyMutation.isPending}
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              {replyMutation.isPending
                ? 'Envoi en cours…'
                : 'Envoyer la réponse'}
            </Button>
          </div>
        </form>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => statusMutation.mutate('open')}
          disabled={statusMutation.isPending}
          className="gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          {statusMutation.isPending ? 'Réouverture…' : 'Rouvrir la demande'}
        </Button>
      )}

      {!isDone && (
        <details className="rounded-xl bg-neutral-100 px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium text-neutral-700">
            Réponse envoyée depuis votre messagerie ?
          </summary>
          <div className="mt-3 flex flex-col items-start gap-2 border-t border-neutral-200 pt-3">
            <p className="text-sm text-neutral-600">
              Consignez seulement la clôture. Aucun faux message ne sera ajouté
              au fil.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => closeMutation.mutate('external_email')}
              disabled={closeMutation.isPending}
            >
              Consigner et clôturer
            </Button>
          </div>
        </details>
      )}
    </section>
  );
}

function queueEmptyMessage(queue: AdminSupportQueue) {
  if (queue === 'action_required')
    return 'Aucune demande ne nécessite votre intervention.';
  if (queue === 'waiting') return 'Aucune demande en attente du client.';
  return 'Aucune demande terminée.';
}

export function AdminSupportPage() {
  const [queue, setQueue] = useState<AdminSupportQueue>('action_required');
  const [page, setPage] = useState(1);
  const [selectedTicketUuid, setSelectedTicketUuid] = useState<string | null>(
    null
  );
  const { data, isLoading, isFetching, isError, refetch } =
    useAdminSupportTickets(queue, page);
  const { data: stats } = useAdminSupportStats();

  if (selectedTicketUuid) {
    return (
      <div className="mx-auto max-w-3xl">
        <AdminTicketDetail
          ticketUuid={selectedTicketUuid}
          onBack={() => setSelectedTicketUuid(null)}
        />
      </div>
    );
  }

  const counts: Record<AdminSupportQueue, number | undefined> = {
    action_required: stats ? stats.open + stats.inProgress : undefined,
    waiting: stats?.waitingForUser,
    done: stats ? stats.resolved + stats.closed : undefined,
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="border-b border-neutral-200 pb-5">
        <h1 className="text-2xl font-bold text-neutral-900">Support</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Les demandes sont classées selon la prochaine personne qui doit agir.
        </p>
      </header>

      <nav
        aria-label="Files de support"
        className="flex gap-1 overflow-x-auto border-b border-neutral-200"
      >
        {QUEUES.map(item => (
          <button
            key={item.value}
            type="button"
            onClick={() => {
              setQueue(item.value);
              setPage(1);
            }}
            className={cn(
              'focus-visible:ring-primary-500 -mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none',
              queue === item.value
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-neutral-600 hover:text-neutral-900'
            )}
            aria-current={queue === item.value ? 'page' : undefined}
          >
            {item.label}
            {counts[item.value] !== undefined && (
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700 tabular-nums">
                {counts[item.value]}
              </span>
            )}
          </button>
        ))}
      </nav>

      {isLoading && <Loading className="py-16" />}

      {isError && (
        <div className="border-error-200 bg-error-50 rounded-2xl border p-6 text-center">
          <AlertCircle className="text-error-600 mx-auto h-6 w-6" />
          <p className="text-error-900 mt-2 font-medium">
            La file de support n’a pas pu être chargée.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
            className="mt-4 gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </Button>
        </div>
      )}

      {!isLoading && !isError && data?.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-white px-6 py-12 text-center">
          <Inbox className="mx-auto h-8 w-8 text-neutral-400" />
          <p className="mt-3 font-medium text-neutral-900">
            {queueEmptyMessage(queue)}
          </p>
        </div>
      )}

      {!isLoading && !isError && data && data.data.length > 0 && (
        <div className={cn(isFetching && 'opacity-70')} aria-live="polite">
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {data.data.map(ticket => (
              <TicketRow
                key={ticket.uuid}
                ticket={ticket}
                onSelect={() => setSelectedTicketUuid(ticket.uuid)}
              />
            ))}
          </div>

          {data.pagination.totalPages > 1 && (
            <nav
              aria-label="Pagination de la file de support"
              className="mt-4 flex items-center justify-between"
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPage(current => current - 1)}
                disabled={!data.pagination.hasPrevious || isFetching}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Précédent
              </Button>
              <span className="text-sm text-neutral-500 tabular-nums">
                Page {data.pagination.currentPage} sur{' '}
                {data.pagination.totalPages}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPage(current => current + 1)}
                disabled={!data.pagination.hasNext || isFetching}
                className="gap-1"
              >
                Suivant
                <ChevronRight className="h-4 w-4" />
              </Button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}
